import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../modal-new";
import VideoUpload from "../inputs/video-upload";
import { getMovementOptions } from "../../utils/exercise-history";
import { SearchDropdown } from "../inputs/search-dropdown";
import { getInitialValue } from "../../utils/complex";
import { RillContext } from "../../rill/RillReact/share/base";

const AddComplex = ({ complex, exerciseLibrary }) => {
  const { t } = useTranslation("coaching-portal");
  const [first, setFirst] = useState(getInitialValue(complex?.exercises[0]));
  const [second, setSecond] = useState(getInitialValue(complex?.exercises[1]));
  const [third, setThird] = useState(getInitialValue(complex?.exercises[2]));
  const [videoUrl, setVideoUrl] = useState(complex?.video?.url || "");
  const [videoFile, setVideoFile] = useState(null);
  const [videoOriginal, setVideoOriginal] = useState(
    complex?.video?.videoOriginal || ""
  );
  const [videoDeleteOtherExercise, setVideoDeleteOtherExercise] =
    useState(null);

  const handleVideoInputChange = (e) => {
    if (e.target.name === "videoUrl") {
      setVideoUrl(e.target.value);
    } else if (e.target.name === "videoFile") {
      setVideoFile(e.target.value);
    }
  };

  const cleanVideoField = () => {
    setVideoOriginal("");
    setVideoDeleteOtherExercise(true);
  };

  const movementOptions = useMemo(() => {
    const movements = getMovementOptions(exerciseLibrary);
    return movements.filter(({ primary }) => primary !== "assistant");
  }, [exerciseLibrary]);

  const isNew = !complex || !complex._id;

  return (
    <form
      action={`/actions/complex/create/${complex?._id || ""}`}
      method="POST"
      data-noreset
      encType="multipart/form-data"
      id="add-complex"
    >
      <Modal
        title={t("addComplex.modalTitle", {
          action: isNew ? t("common:add") : t("common:edit"),
        })}
        className="add-movement-modal"
        toClear={["complex"]}
      >
        <div className="body">
          <input
            name="videoDeleteOtherExercise"
            value={videoDeleteOtherExercise}
            type="hidden"
          />

          <div className="flex items-center gap-4">
            <div>
              <label htmlFor="first">
                {t("addComplex.selectExerciseComplex1")}
              </label>
              <input
                type="hidden"
                name="exercises[0][primary]"
                value={first?.primary}
              />
              <input
                type="hidden"
                name="exercises[0][movement]"
                value={first?.secondary}
              />
              <SearchDropdown
                options={movementOptions}
                handleChange={setFirst}
                placeholder={t("addComplex.selectExercise")}
                value={first?.label}
                disabled={!isNew}
                overlay
              />
            </div>

            <div>
              <label htmlFor="second">
                {t("addComplex.selectExerciseComplex2")}
              </label>
              <input
                type="hidden"
                name="exercises[1][primary]"
                value={second?.primary}
              />
              <input
                type="hidden"
                name="exercises[1][movement]"
                value={second?.secondary}
              />
              <SearchDropdown
                options={movementOptions}
                handleChange={setSecond}
                placeholder={t("addComplex.selectExercise")}
                value={second?.label}
                disabled={!isNew}
                overlay
              />
            </div>

            <div>
              <label htmlFor="third">
                {t("addComplex.selectExerciseComplex3Optional")}
              </label>
              {!!third && (
                <>
                  <input
                    type="hidden"
                    name="exercises[2][primary]"
                    value={third?.primary}
                  />
                  <input
                    type="hidden"
                    name="exercises[2][movement]"
                    value={third?.secondary}
                  />
                </>
              )}
              <SearchDropdown
                options={movementOptions}
                handleChange={setThird}
                placeholder={t("addComplex.selectExercise")}
                value={third?.label}
                disabled={!isNew}
                overlay
              />
            </div>
          </div>

          <div className="bg-(--footer-background-color) px-4 py-2 min-h-14 mt-4 flex items-center gap-6 rounded-xl">
            <span className="opacity-70">{t("addComplex.loadBasedOn")}</span>

            <div className="flex items-center gap-2">
              {!!first && (
                <div className="flex items-center gap-1 bg-(--white-05) rounded-lg p-1">
                  <input
                    type="radio"
                    name="loadBasedOn"
                    id={first.value}
                    value={0}
                    defaultChecked={complex?.loadBasedOn === 0 || !complex}
                  />
                  <label htmlFor={first.value} className="mb-0 opacity-100">
                    {first.label}
                  </label>
                </div>
              )}

              {!!second && (
                <div className="flex items-center gap-1 bg-(--white-05) rounded-lg p-1">
                  <input
                    type="radio"
                    name="loadBasedOn"
                    id={second.value}
                    value={1}
                    defaultChecked={complex?.loadBasedOn === 1}
                  />
                  <label htmlFor={second.value} className="mb-0 opacity-100">
                    {second.label}
                  </label>
                </div>
              )}

              {!!third && (
                <div className="flex items-center gap-1 bg-(--white-05) rounded-lg p-1">
                  <input
                    type="radio"
                    name="loadBasedOn"
                    id={third.value}
                    value={2}
                    defaultChecked={complex?.loadBasedOn === 2}
                  />
                  <label htmlFor={third.value} className="mb-0 opacity-100">
                    {third.label}
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="my-6 h-px bg-(--white-10)" />

          <VideoUpload
            videoUrl={videoUrl}
            videoFile={videoFile}
            videoOriginal={videoOriginal}
            cleanVideoField={cleanVideoField}
            handleInputChange={handleVideoInputChange}
            form="add-complex"
          />
          <p>
            {t("addComplex.movementWillBeSavedToLibrary", {
              action: isNew ? t("addComplex.added") : t("addComplex.saved"),
            })}
          </p>
        </div>

        <footer>
          <a
            role="button"
            data-button-type="text-white"
            data-action="cancel"
            href="/actions/refresh?modal=&complex="
          >
            {t("common:cancel")}
          </a>
          <button data-button-type="primary" type="submit">
            {isNew ? t("common:add") : t("common:save")}
          </button>
        </footer>
      </Modal>
    </form>
  );
};

export default AddComplex;
